import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

/* ============================================================
   Learner documents — Employment Confirmation Letter, Salary
   Schedule (Employment & Labour) and UI-19 — drawn over the
   client's own Word templates, exported blank to the private
   bucket documents/doc-templates/*.pdf (docs/learner-documents.md).

   Pure: no DOM, no fetch. The browser (learner-docs.js) and the
   WhatsApp sender (api/lib/learner-docs-server.js) both call it.

   The values are SETTINGS per cohort (Learner documents › Set-up)
   until the client confirms the rules; each kind of field has a
   source switch to take it from the system instead:
     sources.period   UI-19 termination reason
     sources.amounts  salary, UIF, UI-19 remuneration
     sources.hours    hours per month, UI-19 total hours
   Name, ID number and the PERIOD always come from the system
   (learner_document_data: class dates → the learner's own start /
   exit → set by hand).
============================================================ */

/* The process memo (2026-11-03) is a fixed page — the client's signed, stamped UIF memo, printed
   as it is, first in the pack. Each cohort picks its version (config.memo_file, a file in
   documents/doc-templates/); none = no memo page. */
export const DOC_KINDS = ['memo', 'letter', 'salary', 'ui19'];
export const DOC_LABELS = {
  memo:   'Process memo',
  letter: 'Confirmation letter',
  salary: 'Salary schedule',
  ui19:   'UI-19',
};
export const DOC_TEMPLATE_FILES = {
  letter: 'letter.pdf',
  salary: 'salary-schedule.pdf',
  ui19:   'ui19.pdf',
};
export const MEMO_FILES = {
  'memo.pdf': 'UIF process memo (15 Oct 2026; Mr Klaas and Mr Mbangeni)',
};

/**
 * The template files a set of documents needs: key → file in doc-templates/. Fixed kinds are keyed
 * by kind; each memo version by `memo:<file>`. The loaders fetch these, buildLearnerDocsPdf draws on them.
 */
export function templateKeys(docs, kinds = DOC_KINDS) {
  const keys = new Map();
  for (const k of kinds) {
    if (k === 'memo') { for (const d of docs) if (d.memo_file) keys.set(`memo:${d.memo_file}`, d.memo_file); }
    else keys.set(k, DOC_TEMPLATE_FILES[k]);
  }
  return keys;
}

/* Defaults confirmed by Lubabalo 2026-10-29: salary is the GROSS R2,000 (R20 UIF = 1%, R1,980 net);
   160 hours worked per month. The client's templates printed R1,980 / 24 hours / 144 on the UI-19. */
export const DEFAULT_CONFIG = {
  send_with_payslips: false,                            // retired 2026-11-03; only old WhatsApp requests read it
  issue_date: null,                                     // null = today
  memo_file: null,                                      // the process memo for this cohort (MEMO_FILES); null = none
  sources: { period: 'settings', amounts: 'settings', hours: 'settings' },
  month_default: { salary: 2000, hours: 160, uif: 20, contributor: '' },
  schedule: [],
  ui19: { remuneration: 2000, total_hours: 160, termination_code: '5', contributor: 'YES', non_contributor_reason: '' },
  system: { amount_mode: 'gross', hours_mode: 'fixed', hours_value: 160 },
};

export function withDefaults(config) {
  const c = config || {};
  return {
    ...DEFAULT_CONFIG,
    ...c,
    sources:       { ...DEFAULT_CONFIG.sources,       ...(c.sources || {}) },
    month_default: { ...DEFAULT_CONFIG.month_default, ...(c.month_default || {}) },
    schedule:      Array.isArray(c.schedule) ? c.schedule : [],
    ui19:          { ...DEFAULT_CONFIG.ui19,          ...(c.ui19 || {}) },
    system:        { ...DEFAULT_CONFIG.system,        ...(c.system || {}) },
  };
}

/* ── Dates (ISO strings only — no Date objects, so no timezone drift) ── */

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function parts(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return { y, m, d };
}
const pad2 = (n) => String(n).padStart(2, '0');

/** '2025-10-01', '2026-03-31' → ['2025-10-01', '2025-11-01', … '2026-03-01'] */
export function monthsBetween(startIso, endIso) {
  if (!startIso || !endIso) return [];
  let { y, m } = parts(startIso);
  const end = parts(endIso);
  const out = [];
  while ((y < end.y || (y === end.y && m <= end.m)) && out.length < 60) {
    out.push(`${y}-${pad2(m)}-01`);
    m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

export const fmtLong  = (iso) => { const { y, m, d } = parts(iso); return `${pad2(d)} ${MONTHS[m - 1]} ${y}`; };          // 01 October 2025
export const fmtShort = (iso) => { const { y, m, d } = parts(iso); return `${pad2(d)} ${MONTHS[m - 1].slice(0, 3)} ${y}`; }; // 01 Oct 2025
export const fmtUpper = (iso) => { const { y, m, d } = parts(iso); return `${pad2(d)}-${MONTHS[m - 1].slice(0, 3).toUpperCase()}-${y}`; }; // 01-OCT-2025
const ddmmyy = (iso) => { const { y, m, d } = parts(iso); return `${pad2(d)}${pad2(m)}${String(y).slice(2)}`; };

export function todayIso(tz = 'Africa/Johannesburg') {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

/* ── Money ──────────────────────────────────────────────────── */
const toCents = (n) => Math.round(Number(n || 0) * 100);
/** 1980 → "1980-00" (the way the client fills the salary schedule) */
export const fmtDash = (n) => { const c = toCents(n); return `${Math.floor(c / 100)}-${pad2(c % 100)}`; };
/** 1980 → { rands: "1 980", cents: "00" } (UI-19 columns R and c) */
export const fmtRc = (n) => {
  const c = toCents(n);
  return { rands: String(Math.floor(c / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ' '), cents: pad2(c % 100) };
};

export function initialsOf(firstName) {
  return String(firstName || '').trim().split(/\s+/).filter(Boolean).map((w) => w[0].toUpperCase()).join('');
}

/* ── Resolve: settings + (optional) system data → what gets printed ── */

/**
 * @param {object} p
 * @param {{first_name, surname, national_id}} p.learner  from the learner record
 * @param {object} p.cohort  cohorts row (cohort_id, name, config)
 * @param {object} p.sys     learner_document_data().doc for this learner: the period always comes from
 *                           here (class dates → learner's own dates → set by hand)
 * @param {string} [p.today]  ISO date used when config.issue_date is blank
 */
export function resolveLearnerDoc({ learner, cohort, sys, today = todayIso() }) {
  const cfg = withDefaults(cohort.config);
  const src = cfg.sources;
  const reasonSys  = src.period  === 'system';     // sources.period now only picks the UI-19 termination reason
  const amountsSys = src.amounts === 'system';
  const hoursSys   = src.hours   === 'system';

  const start = String(sys.period_start).slice(0, 10);
  const end   = String(sys.period_end).slice(0, 10);

  const months = monthsBetween(start, end).map((month) => {
    const set = cfg.schedule.find((r) => String(r.month).slice(0, 10) === month) || cfg.month_default;
    const s   = sys?.months?.find((r) => String(r.month).slice(0, 10) === month) || null;
    return {
      month,
      salary: amountsSys ? Number(s?.salary ?? 0) : Number(set.salary ?? 0),
      uif:    amountsSys ? Number(s?.uif ?? 0)    : Number(set.uif ?? 0),
      hours:  hoursSys   ? Number(s?.hours ?? 0)  : Number(set.hours ?? 0),
      contributor: amountsSys ? (s?.contributor ? 'Contributor' : 'Non-contributor') : String(set.contributor ?? ''),
    };
  });

  const surname = String(learner.surname || '').trim();
  const first   = String(learner.first_name || '').trim();

  return {
    full_name:   [first, surname].filter(Boolean).join(' '),
    surname,
    initials:    initialsOf(first),
    national_id: String(learner.national_id || '').trim(),
    cohort_id:    cohort.cohort_id,
    cohort_name:  cohort.name,
    class_name:   sys.class_name || '',
    period_start: start,
    period_end:   end,
    period_basis: sys.period_basis,
    issue_date:  cfg.issue_date || today,
    months,
    ui19: {
      remuneration:     amountsSys ? Number(sys.ui19_monthly ?? 0) : Number(cfg.ui19.remuneration ?? 0),
      total_hours:      hoursSys   ? Number(sys.total_hours ?? 0)  : Number(cfg.ui19.total_hours ?? 0),
      termination_code: reasonSys  ? String(sys.termination_code ?? '') : String(cfg.ui19.termination_code ?? ''),
      contributor:      String(cfg.ui19.contributor ?? ''),
      non_contributor_reason: String(cfg.ui19.non_contributor_reason ?? ''),
    },
    sources: { ...src },
    warnings: [...(sys?.warnings || [])],
  };
}

/**
 * Resolve many learners at once. A learner's cohort is their class's, or the one set for them
 * (learner_document_data().doc.cohort_id); cohortOverride (an unsaved cohort row, for Preview)
 * replaces it. Shared by the browser and the WhatsApp sender, so both print the same.
 */
export function resolveLearnerDocs({ studentUuids, cohorts, sysRows, learners, cohortOverride = null }) {
  const sysBy = new Map((sysRows || []).map((r) => [r.student_uuid, r.doc]));
  const learnerBy = new Map((learners || []).map((r) => [r.student_uuid, r]));
  const docs = [], skipped = [];
  for (const id of studentUuids) {
    const learner = learnerBy.get(id);
    if (!learner) { skipped.push({ student_uuid: id, reason: 'Learner not found' }); continue; }
    const sys = sysBy.get(id);
    if (!sys) { skipped.push({ student_uuid: id, reason: 'No cohort: not in an enrolled class — set a cohort for this learner' }); continue; }
    if (!sys.period_start) { skipped.push({ student_uuid: id, reason: 'No start date: set a period for this learner' }); continue; }
    const cohort = cohortOverride || (cohorts || []).find((k) => k.cohort_id === Number(sys.cohort_id));
    if (!cohort) { skipped.push({ student_uuid: id, reason: 'Cohort not found' }); continue; }
    docs.push({
      student_uuid: id,
      send_with_payslips: withDefaults(cohort.config).send_with_payslips === true,
      memo_file: withDefaults(cohort.config).memo_file || null,
      ...resolveLearnerDoc({ learner, cohort, sys }),
    });
  }
  return { docs, skipped };
}

/** Warning codes from learner_document_data(), in words. */
export const WARNING_TEXT = {
  invalid_id:              'ID number is not 13 digits',
  no_start_date:           'no start date',
  no_end_date:             'no end date: printed up to today',
  period_end_before_start: 'exit date is before the start: set the period by hand',
  exit_date_from_register: 'withdrawn: end date taken from the last paid day',
  withdrawn_no_exit_date:  'withdrawn with no exit date',
  in_several_classes:      'in more than one class: the one with the most paid days is used',
  no_class:                'not in an enrolled class',
  no_paid_months:          'no paid months in the period',
};

/* ── Drawing ────────────────────────────────────────────────────
   Coordinates are PDF points, origin bottom-left, measured on the
   Word exports of the templates (pdfminer); y values are baselines.
   If a template is re-exported, re-measure (docs/learner-documents.md). */

const INK = rgb(0, 0, 0);

const LETTER = {
  date:   { x: 88.4,  y: 634.2, size: 12 },          // after "Date: " (bold)
  name:   { x: 236.3, y: 453.9, size: 12 },          // after "Learner Name and Surname :"
  id:     { x: 236.3, y: 426.3, size: 12 },
  period: { x: 236.3, y: 398.7, size: 12, maxW: 330 },
};

const SALARY = {
  size: 9.5,
  id:       { x: 276, y: 672.3, maxW: 280 },
  name:     { x: 276, y: 658.7, maxW: 280 },
  from:     { x: 321, y: 616.6, maxW: 75 },
  to:       { x: 445, y: 616.6, maxW: 95 },
  // 17 table rows: bottom border of each row
  rowBottoms: [538.3, 520.3, 502.5, 484.8, 467.4, 448.1, 430.0, 412.3, 394.2, 375.4, 356.9, 338.4, 321.1, 304.1, 284.9, 266.4, 250.1],
  rowLift: 6,
  cols: { period: 55, freq: 145.5, salary: 215.5, hours: 275.5, uif: 379, contributor: 472.5 },
  contributorW: 72,
  date:     { x: 102, y: 159.3, maxW: 70 },
};

const UI19 = {
  row:    { y: 302.7, size: 8 },
  surname:  { x: 22.9, maxW: 108 },
  initials: { x: 136.5, maxW: 28 },
  // ID cells: borders of the 13 boxes in column C
  idBorders: [169.4, 183.9, 198.4, 212.9, 227.4, 241.9, 256.5, 271.0, 285.5, 299.9, 314.3, 328.9, 343.4, 357.9],
  rands:  { x: 360.9 },
  cents:  { x: 409.5 },
  hours:  { x: 440.4 },
  startBorders: [474.8, 491.7, 508.8, 525.7, 542.7, 559.6, 576.4],
  endBorders:   [576.4, 593.2, 609.9, 626.5, 642.9, 659.6, 676.3],
  reason:      { x: 693.3 },
  contributor: { x: 744.3 },
  nonContrib:  { x: 800 },
  period: { x: 542.1, y: 561.5, size: 8, maxW: 125 },   // "for the month of" box
  date:   { x: 490.1, y: 166.4, size: 11 },              // after "DATE" (bold)
};

/** Shrink-to-fit single line. */
function put(page, font, text, x, y, size, maxW) {
  const t = String(text ?? '');
  if (!t) return;
  let s = size;
  if (maxW) while (s > 5 && font.widthOfTextAtSize(t, s) > maxW) s -= 0.25;
  page.drawText(t, { x, y, size: s, font, color: INK });
}

/** One character centred in each box between consecutive borders. */
function boxes(page, font, chars, borders, y, size) {
  const list = [...String(chars ?? '')];
  for (let i = 0; i < list.length && i < borders.length - 1; i++) {
    const w = font.widthOfTextAtSize(list[i], size);
    page.drawText(list[i], { x: (borders[i] + borders[i + 1]) / 2 - w / 2, y, size, font, color: INK });
  }
}

function drawLetter(page, f, d) {
  put(page, f.helvB, fmtLong(d.issue_date), LETTER.date.x, LETTER.date.y, LETTER.date.size);
  put(page, f.helv,  d.full_name,           LETTER.name.x, LETTER.name.y, LETTER.name.size, 330);
  put(page, f.helv,  d.national_id,         LETTER.id.x,   LETTER.id.y,   LETTER.id.size);
  put(page, f.helv,  `${fmtLong(d.period_start)} – ${fmtLong(d.period_end)}`,
      LETTER.period.x, LETTER.period.y, LETTER.period.size, LETTER.period.maxW);
}

function drawSalary(page, f, d) {
  const S = SALARY, z = S.size;
  put(page, f.helv, d.national_id,                         S.id.x,   S.id.y,   z, S.id.maxW);
  put(page, f.helv, `${d.initials} ${d.surname}`.trim(),   S.name.x, S.name.y, z, S.name.maxW);
  put(page, f.helv, fmtUpper(d.period_start),              S.from.x, S.from.y, z, S.from.maxW);
  put(page, f.helv, fmtUpper(d.period_end),                S.to.x,   S.to.y,   z, S.to.maxW);
  d.months.slice(0, S.rowBottoms.length).forEach((m, i) => {
    const y = S.rowBottoms[i] + S.rowLift;
    put(page, f.helv, fmtShort(m.month),  S.cols.period, y, z);
    put(page, f.helv, 'Monthly',          S.cols.freq,   y, z);
    put(page, f.helv, fmtDash(m.salary),  S.cols.salary, y, z);
    put(page, f.helv, String(m.hours),    S.cols.hours,  y, z);
    put(page, f.helv, fmtDash(m.uif),     S.cols.uif,    y, z);
    put(page, f.helv, m.contributor,      S.cols.contributor, y, z, S.contributorW);
  });
  put(page, f.helv, fmtLong(d.issue_date), S.date.x, S.date.y, z, S.date.maxW);
}

function drawUi19(page, f, d) {
  const U = UI19, y = U.row.y, z = U.row.size, t = f.times;
  put(page, t, `${fmtShort(d.period_start)} – ${fmtShort(d.period_end)}`, U.period.x, U.period.y, U.period.size, U.period.maxW);
  put(page, t, d.surname.toUpperCase(), U.surname.x, y, z, U.surname.maxW);
  put(page, t, d.initials,              U.initials.x, y, z, U.initials.maxW);
  boxes(page, t, d.national_id.replace(/\s+/g, ''), U.idBorders, y, z);
  const rc = fmtRc(d.ui19.remuneration);
  put(page, t, rc.rands, U.rands.x, y, z);
  put(page, t, rc.cents, U.cents.x, y, z);
  put(page, t, String(d.ui19.total_hours), U.hours.x, y, z);
  boxes(page, t, ddmmyy(d.period_start), U.startBorders, y, z);
  boxes(page, t, ddmmyy(d.period_end),   U.endBorders,   y, z);
  put(page, t, d.ui19.termination_code,       U.reason.x, y, z);
  put(page, t, d.ui19.contributor,            U.contributor.x, y, z);
  put(page, t, d.ui19.non_contributor_reason, U.nonContrib.x, y, z, 28);
  put(page, f.timesB, fmtShort(d.issue_date), U.date.x, U.date.y, U.date.size);
}

const DRAW = { memo: () => {}, letter: drawLetter, salary: drawSalary, ui19: drawUi19 };

/**
 * One PDF: for each resolved learner, one page per requested document (no memo page when the
 * cohort has none).
 * @param {object[]} docs        resolveLearnerDocs() results
 * @param {Record<string, ArrayBuffer|Uint8Array>} templates  keyed as templateKeys()
 * @param {string[]} [kinds]     which documents, in order
 * @returns {Promise<Uint8Array>}
 */
export async function buildLearnerDocsPdf(docs, templates, kinds = DOC_KINDS) {
  const pdf = await PDFDocument.create();
  const f = {
    helv:   await pdf.embedFont(StandardFonts.Helvetica),
    helvB:  await pdf.embedFont(StandardFonts.HelveticaBold),
    times:  await pdf.embedFont(StandardFonts.TimesRoman),
    timesB: await pdf.embedFont(StandardFonts.TimesRomanBold),
  };
  const bg = {};
  for (const key of templateKeys(docs, kinds).keys()) {
    const src = await PDFDocument.load(templates[key]);
    [bg[key]] = await pdf.embedPdf(src, [0]);
  }
  for (const d of docs) {
    for (const k of kinds) {
      const key = k === 'memo' ? (d.memo_file && `memo:${d.memo_file}`) : k;
      if (!key) continue;
      const page = pdf.addPage([bg[key].width, bg[key].height]);
      page.drawPage(bg[key], { x: 0, y: 0 });
      DRAW[k](page, f, d);
    }
  }
  pdf.setTitle(docs.length === 1 ? `Learner documents — ${docs[0].full_name}` : `Learner documents (${docs.length})`);
  return pdf.save();
}
